import { Address } from '@liberty-financial/agp-shared';
import * as _ from 'lodash';

import { OpportunityAddress } from '@features/opportunities/modules/core/models/address';
import { OpportunitySchema } from '@features/opportunities/modules/core/models/schema-property-definition';
import { OpportunitySchemaUtil } from '@features/opportunities/modules/core/utils/opportunity-schema-util';
import { SparkAddress } from '@shared/models/address';

import { Applicant } from '../applicant';
import { BaseApplicant } from '../base-applicant';
import { SharePercentage } from '../share-percentage.contract';

export * from './shareholder';

export namespace Company {
  export interface SchemaPropertyDefinition
    extends BaseApplicant.SchemaPropertyDefinition {
    //TODO: Fix ESLINT style config
    name: OpportunitySchema.StringPropertyDefinition;
    tradingName: OpportunitySchema.StringPropertyDefinition;
    companyType: OpportunitySchema.StringPropertyDefinition;
    businessNumber: OpportunitySchema.StringPropertyDefinition;
    companyNumber: OpportunitySchema.StringPropertyDefinition;
    startDate: OpportunitySchema.StringPropertyDefinition;
    gstRegistrationDate: OpportunitySchema.StringPropertyDefinition;
    businessNature: OpportunitySchema.OptionsPropertyDefinition;
    accountant: {
      name: OpportunitySchema.StringPropertyDefinition;
      phone: OpportunitySchema.StringPropertyDefinition;
    };
    address: OpportunityAddress.SchemaPropertyDefinition;
    registeredOffice: OpportunityAddress.SchemaPropertyDefinition;
    directors: OpportunitySchema.StringArrayPropertyDefinition;
  }

  export interface SchemaContract extends BaseApplicant.SchemaContract {
    name: string;
    tradingName: string | null;
    businessNumber: string | null;
    companyNumber: string | null;
    startDate: string | null;
    gstRegistrationDate: string | null;
    businessNature: string | null;
    accountant?: {
      name: string | null;
      phone: string | null;
    };
    address: SparkAddress.SchemaContract | null;
    registeredOffice: SparkAddress.SchemaContract | null;
    directors: string[];
    shareholders: SharePercentage.Collection.SchemaContract;
  }

  export interface CompanySummary {
    id: string;
    name: string;
  }

  export interface Model extends BaseApplicant.Model {
    id: string;
    name: string;
    tradingName?: string | null;
    businessNumber?: string | null;
    companyNumber?: string | null;
    startDate?: string | null;
    gstRegistrationDate?: string | null;
    businessNature?: string | null;
    accountant?: {
      name: string | null;
      phone: string | null;
    };
    address: Address | null;
    registeredOffice: Address | null;
    directors: string[];
    shareholders: SharePercentage.Collection.Model;
  }

  export function toModel(id: string, c: SchemaContract): Model {
    return {
      id,
      ...c,
      shareholders: SharePercentage.Collection.toModel(c.shareholders),
      address: SparkAddress.toModel(c.address),
      registeredOffice: SparkAddress.toModel(c.registeredOffice)
    };
  }

  export function toSchemaContract(c: Partial<Model>): Partial<SchemaContract> {
    return {
      ...c,
      startDate: OpportunitySchemaUtil.Transform.Date.toSchemaContract(
        c.startDate
      ),
      gstRegistrationDate:
        OpportunitySchemaUtil.Transform.Date.toSchemaContract(
          c.gstRegistrationDate
        ),
      shareholders: c.shareholders
        ? SharePercentage.Collection.toSchemaContract(c.shareholders)
        : undefined,
      address: c.address ? SparkAddress.toSchemaContract(c.address) : undefined,
      registeredOffice: c.registeredOffice
        ? SparkAddress.toSchemaContract(c.registeredOffice)
        : undefined
    };
  }

  export function getEligibleDirectors(
    applicants: Applicant.Summary[]
  ): Applicant.Summary[] {
    return applicants
      .filter((a) => a.applicantType === Applicant.Type.Individual)
      .map((a) => ({ ...a, id: OpportunitySchemaUtil.idFromReference(a.id) })); // TODO: Need to change this by referencing entity id in the component
  }

  export function toApplicantSummary(
    id: string,
    c: SchemaContract
  ): Applicant.Summary {
    return Model.toApplicantSummary(toModel(id, c));
  }

  export namespace Model {
    export function toApplicantSummary(
      model: Company.Model
    ): Applicant.Summary {
      return {
        id: `companies/${model.id}`,
        entityId: model.id,
        displayName: model.name,
        role: model.role ?? '',
        applicantType: Applicant.Type.Company,
        contactNumber: model.accountant?.phone ?? '',
        description: `${model.businessNature || ''}`.trim(),
        email: null
      };
    }
  }
}
